import { Alert, Button, Row, notification } from 'antd';
import styled from 'styled-components';
import { Link, useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowRightOutlined, PlusOutlined } from '@ant-design/icons';
import { useAuthContext } from '../../contexts/AuthContext';
import { useDashboardCards } from './utils/useOwnerPensionerDashboardCards';
import { ContentCustom, PageHeaderCustom } from '../../components';
import { AnounceInfoModal } from './utils/AnounceInfoModal';
import { setConsultingAnounceAuthorization } from './utils/setConsultingAnounceAuthorization';
import { useCallsAPI } from '../usersShows/utils/callAPI';

const StyledCards = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 20px;
`;

const StyledCard = styled(Link)`
  display: flex;
  align-items: center;
  gap: 18px;
  height: 100%;
  padding: 20px 22px;
  background: var(--ds-surface);
  border-radius: var(--ds-radius-xl);
  box-shadow: var(--ds-shadow-card);
  color: var(--textColor);
  transition: transform var(--ds-dur-fast) var(--ds-ease),
    box-shadow var(--ds-dur-fast) var(--ds-ease);

  &:hover {
    color: var(--textColor);
    transform: translateY(-2px);
    box-shadow: var(--ds-shadow-card-hover);

    .card-arrow {
      color: var(--ds-cambridge-dark);
      transform: translateX(2px);
    }
  }

  .card-icon {
    display: flex;
    flex-shrink: 0;
    align-items: center;
    justify-content: center;
    width: 56px;
    height: 56px;
    border-radius: 14px;
    background: var(--ds-column-bg);

    img {
      width: 32px;
      height: 32px;
    }
  }

  .card-text {
    display: flex;
    flex: 1;
    flex-direction: column;
    gap: 4px;
    min-width: 0;
  }

  .card-title {
    font-size: 16px;
    font-weight: 700;
  }

  .card-content {
    font-size: 13px;
    line-height: 1.45;
    color: var(--dimGray);
  }

  .card-arrow {
    flex-shrink: 0;
    font-size: 14px;
    color: var(--lightGray);
    transition: color var(--ds-dur-fast) var(--ds-ease),
      transform var(--ds-dur-fast) var(--ds-ease);
  }
`;

/**
 * OwnerPensionerDashboard component for rendering the dashboard based on user role.
 *
 * @component
 * @returns {React.ReactNode} - The rendered component.
 */
export const OwnerPensionerDashboard = () => {
  const {
    user: { role },
    pensionerInfo,
    ownerInfo,
    getUserProfiles
  } = useAuthContext();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [authorizationValid, setAuthorizationValid] = useState(false);
  const { pensionerCards, ownerCards } = useDashboardCards({
    authorizationValid,
    pensionerID: pensionerInfo?._id,
    ownerID: ownerInfo?._id,
    role
  });
  const [anounceModalOpen, setAnounceModalOpen] = useState(false);
  const [missingConditionInfo, setMissingConditionInfo] = useState({
    status: '',
    principalProfileNecessaryDocuments: {},
    secondaryProfileNecessaryDocuments: {}
  });
  const [displayInfos, setDisplayInfos] = useState(false);
  const [profile, setProfile] = useState();

  const { getProfile } = useCallsAPI({
    setFunction: (data) => setProfile(data[0])
  });

  useEffect(() => {
    (async () => {
      await getProfile();
    })();
  }, []);

  const cardsRender = {
    'users:PENSIONER': pensionerCards,
    'users:OWNER': ownerCards
  };

  useEffect(() => {
    (async () => {
      await getUserProfiles();
    })();
  }, []);

  const newUserInformationsStringkeys = ['civility', 'petsitting_address'];
  useEffect(() => {
    if (ownerInfo?._id) {
      if (
        newUserInformationsStringkeys.every((key) => !ownerInfo[key]) ||
        ownerInfo?.animals?.length <= 0
      ) {
        setDisplayInfos(true);
      }
    }
  }, [ownerInfo]);

  useEffect(() => {
    if (Object.keys(pensionerInfo).length) {
      setConsultingAnounceAuthorization({
        pensionerInfo,
        setMissingConditionInfo,
        setAuthorizationValid
      });
    }
  }, [pensionerInfo]);

  return (
    <>
      <PageHeaderCustom
        withoutBreadCrumb
        withoutBackIcon
        title={t('home.title')}
        extra={
          role === 'users:OWNER' && (
            <Button
              type="primary"
              onClick={() => {
                if (profile?.animals?.length) {
                  return navigate('/supervisions/create');
                }
                return notification.warning({
                  message: t('owners.users_shows.messages.create_animals')
                });
              }}
            >
              <PlusOutlined />
              {t('owners.users_shows.buttons.new_supervision_request')}
            </Button>
          )
        }
      />
      <ContentCustom transparent>
        <Row gutter={[16, 16]}>
          {role === 'users:OWNER' && ownerInfo?._id && displayInfos && (
            <Alert
              className="dashboard-alert"
              type="info"
              closable
              message={t(
                'owners.users_shows.messages.new_account_informations.title'
              )}
              description={t(
                'owners.users_shows.messages.new_account_informations.message'
              )}
              showIcon
            />
          )}
        </Row>
        <StyledCards>
          {cardsRender[role].map((card) => (
            <StyledCard
              key={card.title}
              to={card.link}
              onClick={() =>
                authorizationValid ? false : setAnounceModalOpen(true)
              }
            >
              <span className="card-icon">{card.icon}</span>
              <span className="card-text">
                <span className="card-title">{card.title}</span>
                <span className="card-content">{card.content}</span>
              </span>
              <ArrowRightOutlined className="card-arrow" />
            </StyledCard>
          ))}
        </StyledCards>
      </ContentCustom>
      {anounceModalOpen && (
        <AnounceInfoModal
          anounceModalOpen={anounceModalOpen}
          setAnounceModalOpen={setAnounceModalOpen}
          missingConditionInfo={missingConditionInfo}
          pensionerInfo={pensionerInfo}
          t={t}
        />
      )}
    </>
  );
};
